import { isNull, noop } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { useWindowResize } from '../../../../hooks/useWindowResize';
import { capitaliseStartLetter } from '../../../../utils/regex-utils';
import { IconButton } from '../../../shared/button/IconButton';
import { PageToggleButton } from '../../../shared/button/PageToggleButton';
import { Calculator } from '../../../shared/calculator/Calculator';
import { newCalculatorField } from '../../../shared/calculator/constants';
import { Icon } from '../../../shared/icon/Icon';
import { Calculator as CalculatorIcon, CaretDown, CaretSide, Robot, Settings } from '../../../shared/icons';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { CalculatorManagerPage, getAllCalculatorFields, getCurrentCalculatorFieldType, getPadOptionsModalOpen, selectFieldType, setCalculatorManagerPage, togglePadOptionsModal } from '../../calculator/store';
import styles from './CalculatorManager.module.scss';
import { PadOptionsModal } from './PadOptionsModal';
import { CalculatorFieldTypeModal } from './CalculatorFieldTypeModal';
import { PlusButton } from '../../../shared/button/PlusButton';

const { lightGrey } = styles;

const NAV_HEADER_MARGINS = 160;

export const CalculatorManager: React.FC = () => {
    const [openSections, setOpenSections] = useState<string[]>([]);
    const dispatch = useAppDispatch();
    const testId = 'calculator-manager';
    const [screenWidth, screenHeight] = useWindowResize();
    const fieldTypeOptions = useAppSelector(getAllCalculatorFields);
    const padOptionsModalOpen = useAppSelector(getPadOptionsModalOpen);
    const selectedCalculatorFieldType = useAppSelector(getCurrentCalculatorFieldType);

    const wrapperHeight = useMemo(() => screenHeight - NAV_HEADER_MARGINS, [screenHeight]);

    const getSectionOpen = useCallback((calculatorFieldType: string) => openSections.includes(calculatorFieldType), [openSections]);
    const getSectionOpenIcon = useCallback((isOpen: boolean) => isOpen ? CaretDown : CaretSide, []);
    const toggleSection = useCallback((calculatorFieldType: string) => {
        const isOpen = getSectionOpen(calculatorFieldType);
        if (isOpen) {
            return setOpenSections(openSections => openSections.filter(type => type !== calculatorFieldType));
        }
        return setOpenSections(openSections => ([...openSections, calculatorFieldType]));
    }, [getSectionOpen, setOpenSections]);

    const selectCalculatorFieldType = useCallback((calculatorFieldTypeId?: number) => dispatch(selectFieldType(calculatorFieldTypeId)), [dispatch]);
    const openPadOptions = useCallback(() => dispatch(togglePadOptionsModal(true)), [dispatch]);
    const goToPrompts = useCallback(() => dispatch(setCalculatorManagerPage(CalculatorManagerPage.PROMPTS)), [dispatch]);

    return (
        <div className={styles.calculatorManagerWrapper} data-testid={`${testId}-wrapper`}>
            <div className={styles.calculatorManagerHeaderWrapper}>
                <div className={styles.calculatorManagerTitleButtonWrapper}>
                    <div className={styles.calculatorManagerHeaderTitle} data-testid={`${testId}-title`}>Calculator Manager</div>
                    <PlusButton onClick={() => selectCalculatorFieldType()} fontSize={20} />
                    <IconButton onClick={openPadOptions} icon={Settings} testId={testId} fontSize={28} margin='0 0 0 5px' />
                </div>
                <PageToggleButton label='Calculator Prompts' onClick={goToPrompts} icon={Robot} testId={testId} fontSize={25} />
            </div>
            <div className={styles.calculatorFieldTypeContent} style={{ height: wrapperHeight }}>
                <div className={styles.calculatorFieldTypesWrapper}>
                    <Scrollable>
                        {fieldTypeOptions.map(({ includeGracePeriod, includeOutput, includeOther, name, calculatorFieldTypeId }) => {
                            const sectionOpen = getSectionOpen(name);
                            return (
                                <div className={styles.calculatorFieldTypeWrapper} key={calculatorFieldTypeId}>
                                    <div className={styles.calculatorFieldTypeHeader} onClick={() => toggleSection(name)}>
                                        <div className={styles.sectionOpenIcon}>
                                            <Icon icon={getSectionOpenIcon(sectionOpen)} fontSize={15} />
                                        </div>
                                        <div className={styles.calculatorFieldTypeTitle}>{capitaliseStartLetter(name)}</div>
                                    </div>
                                    {sectionOpen &&
                                        <div key={calculatorFieldTypeId} className={styles.calculation}>
                                            <div className={styles.readOnlyCalculatorField}>
                                                <Calculator
                                                    value={[newCalculatorField()]}
                                                    updateValue={noop}
                                                    isEditing
                                                    fieldType={name}
                                                    width={screenWidth * 0.9}
                                                    borderColor={lightGrey}
                                                    showOutputField={includeOutput === 1}
                                                    showGracePeriod={includeGracePeriod === 1}
                                                    includeFreeTextOption={includeOther === 1}
                                                    minHeight='0'
                                                />
                                            </div>
                                            <div className={styles.iconWrapper}>
                                                <IconButton icon={CalculatorIcon} onClick={() => selectCalculatorFieldType(calculatorFieldTypeId)} fontSize={30} />
                                            </div>
                                        </div>
                                    }
                                </div>
                            );
                        }
                        )}
                    </Scrollable>
                </div>
            </div>
            {padOptionsModalOpen && <PadOptionsModal />}
            {!isNull(selectedCalculatorFieldType) && <CalculatorFieldTypeModal calculatorField={selectedCalculatorFieldType} />}
        </div>
    );
};
